import Image from 'next/image';

import { RichInline } from '@/atoms';

import { getRealUrl } from '@/utils/utils';

import type { FunctionComponent } from 'react';

export interface TypeHeroBannerProps {
  backgroundImage?: { url?: string } | null;
  /**
   * Valor CSS de `object-position` para el recorte de `backgroundImage`.
   * Llega ya formateado desde el dynamic-component (p.ej. "30% 70%"). Si
   * `undefined`, el `object-cover` por defecto (center/center) se conserva.
   */
  imagePosition?: string;
  /** RichText desde Strapi (Titulo). Se renderiza con RichInline para preservar marks. */
  title?: string;
  /** Subtitulo (string): línea prominente bajo el H1 (Figma: "Entender tu hipoteca…"). */
  subtitle?: string;
  /** RichText (Descripcion): párrafos del cuerpo bajo el subtítulo; admite saltos de línea/marks. */
  description?: string;
  /** TextStyle opcional para el H1 (override por bloque desde Strapi). */
  tituloStyleClass?: string;
  /** TextStyle opcional para el subtítulo. */
  subtituloStyleClass?: string;
}

/**
 * Variante `Banner` del hero (Figma HoM: Sobre Nosotros, Servicios, etc.).
 * Full-bleed 360px mobile / 515px desktop con gradient horizontal
 * dark→transparent y una curva elíptica en la esquina inferior derecha
 * — la forma "blob" del Figma se modela con `border-bottom-right-radius`
 * elíptico + `overflow-hidden` en lugar de un SVG decorativo (más simple
 * de mantener, no requiere asset por tenant).
 *
 * Pensado para páginas de cabecera donde `transparentMenu=true` en Strapi:
 * `pt-(--header-height)` asegura que el contenido vertical-centrado queda
 * por debajo del header flotante sin recalcular alturas.
 */
export const HeroBanner: FunctionComponent<TypeHeroBannerProps> = ({
  backgroundImage,
  imagePosition,
  title,
  subtitle,
  description,
  tituloStyleClass,
  subtituloStyleClass,
}) => {
  const bgUrl = backgroundImage?.url
    ? getRealUrl(backgroundImage.url)
    : undefined;

  return (
    <section
      // Curva elíptica (blob) sólo en desktop (lg+): 40% ancho × 80% alto
      // via arbitrary-property (`rounded-*` no soporta elipses de 2 valores).
      // En mobile se deja recto para que la curva inferior-derecha no recorte
      // horizontalmente el texto largo.
      className="relative min-h-[420px] w-full overflow-hidden lg:h-[515px] lg:[border-bottom-right-radius:40%_80%]"
    >
      {bgUrl && (
        <Image
          src={bgUrl}
          alt=""
          fill
          priority
          sizes="100vw"
          className="object-cover"
          style={imagePosition ? { objectPosition: imagePosition } : undefined}
        />
      )}

      <div className="from-neutrals-900/55 via-neutrals-900/30 absolute inset-0 bg-gradient-to-r to-transparent" />

      <div className="relative z-10 flex h-full min-h-[420px] w-full flex-col justify-center pt-(--header-height) pb-12 lg:min-h-0 lg:pb-0">
        <div className="u-wrapper mx-auto w-full max-w-(--wrapper-size) px-4">
          <div className="flex max-w-[1100px] flex-col gap-2">
            {title && (
              <h1
                className={
                  tituloStyleClass ??
                  'text-neutrals-0 text-[40px] leading-[44px] font-bold lg:text-[64px] lg:leading-[72px]'
                }
              >
                <RichInline html={title} />
              </h1>
            )}
            {subtitle && (
              <RichInline
                as="p"
                html={subtitle}
                className={
                  subtituloStyleClass ??
                  'text-neutrals-0 text-xl leading-7 lg:text-2xl lg:leading-8'
                }
              />
            )}
            {description && (
              <RichInline
                as="div"
                html={description}
                className="text-neutrals-0 mt-3 text-base leading-6 lg:text-lg lg:leading-7 [&_p]:m-0 [&_p:not(:last-child)]:mb-3"
              />
            )}
          </div>
        </div>
      </div>
    </section>
  );
};

HeroBanner.displayName = 'HeroBanner';
